import azureDevOpsService from './azureDevOpsService';

const BASE = '/api/logs-intel';

function authHeaders() {
  const pat = azureDevOpsService.getPat();
  return pat ? { 'X-Azure-Devops-Pat': pat } : {};
}

async function getJson(url) {
  const res = await fetch(url, { headers: authHeaders() });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `${url} a echoue (HTTP ${res.status})`);
  }
  return res.json();
}

async function patchJson(url, payload) {
  const res = await fetch(url, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify(payload)
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `${url} a echoue (HTTP ${res.status})`);
  }
  return res.json();
}

const logIntelligenceService = {
  async getStats({ since, until, env } = {}) {
    const params = new URLSearchParams();
    if (since) params.set('since', since);
    if (until) params.set('until', until);
    if (env) params.set('env', env);
    return getJson(`${BASE}/stats?${params.toString()}`);
  },

  async getEnvs() {
    return getJson(`${BASE}/envs`);
  },

  // Jusqu'ou les logs remontent encore, par environnement. Sert a ne pas
  // proposer un TraceId qui ne ramenera rien : la retention n'est pas la meme
  // partout (66 jours sur qa2, 6 sur qa4 au 15/10/2026), donc on la demande.
  async getLogsRetention() {
    return getJson(`${BASE}/logs-retention`);
  },

  async getServices() {
    return getJson(`${BASE}/services`);
  },

  async getRuns(limit = 20) {
    return getJson(`${BASE}/runs?limit=${limit}`);
  },

  async getSignatures({ env, service, status, since, until, limit = 200, offset = 0 } = {}) {
    const params = new URLSearchParams();
    if (env) params.set('env', env);
    if (service) params.set('service', service);
    if (status) params.set('status', status);
    if (since) params.set('since', since);
    if (until) params.set('until', until);
    params.set('limit', limit);
    params.set('offset', offset);
    return getJson(`${BASE}/signatures?${params.toString()}`);
  },

  // `env` borne l'historique de la fiche au meme environnement que la liste
  // d'ou on l'a ouverte. Sans lui, une erreur presente sur plusieurs envs
  // affichait les occurrences des quatre - 21,7 % du catalogue est dans ce
  // cas, et 70 % des signatures vues depuis qa1.
  async getSignature(id, { env } = {}) {
    const params = new URLSearchParams();
    if (env) params.set('env', env);
    const query = params.toString();
    return getJson(`${BASE}/signatures/${encodeURIComponent(id)}${query ? `?${query}` : ''}`);
  },

  // Niveau "Probleme" : une ligne par cause plutot qu'une par signature. Le
  // regroupement est fait par le moteur (il doit voir TOUTES les signatures
  // filtrees, pas seulement celles chargees ici) et renvoie
  // { total, signatureTotal, problems[] }.
  async getProblems({ env, service, status, since, until, sort, limit = 1000, offset = 0 } = {}) {
    const params = new URLSearchParams();
    if (env) params.set('env', env);
    if (service) params.set('service', service);
    if (status) params.set('status', status);
    if (since) params.set('since', since);
    if (until) params.set('until', until);
    if (sort) params.set('sort', sort);
    params.set('limit', limit);
    params.set('offset', offset);
    return getJson(`${BASE}/problems?${params.toString()}`);
  },

  // Les signatures d'un probleme, chargees seulement quand la ligne est
  // depliee (le detail complet ferait plusieurs Mo sur la liste entiere).
  // Les memes filtres que la liste sont transmis pour que le detail affiche
  // exactement ce que la ligne compte.
  async getProblemSignatures(id, { env, service, status, since, until } = {}) {
    const params = new URLSearchParams();
    if (env) params.set('env', env);
    if (service) params.set('service', service);
    if (status) params.set('status', status);
    if (since) params.set('since', since);
    if (until) params.set('until', until);
    return getJson(`${BASE}/problems/${encodeURIComponent(id)}/signatures?${params.toString()}`);
  },

  // Corrélation PR : ce qui a été livré par le déploiement qui a précédé
  // l'apparition du problème. Mêmes filtres que la liste, pour la même raison
  // que ci-dessus — l'identifiant d'un problème est calculé à la lecture et
  // n'existe que dans le périmètre qui l'a produit.
  async getProblemCorrelation(id, { env, service, status, since, until } = {}) {
    const params = new URLSearchParams();
    if (env) params.set('env', env);
    if (service) params.set('service', service);
    if (status) params.set('status', status);
    if (since) params.set('since', since);
    if (until) params.set('until', until);
    return getJson(`${BASE}/problems/${encodeURIComponent(id)}/correlation?${params.toString()}`);
  },

  // Les campagnes de test de la période, avec le nombre d'erreurs tombées
  // pendant chacune. Le sens inverse du lien affiché sur une signature.
  async getCampaigns({ env, since, until, limit } = {}) {
    const params = new URLSearchParams();
    if (env) params.set('env', env);
    if (since) params.set('since', since);
    if (until) params.set('until', until);
    if (limit) params.set('limit', String(limit));
    return getJson(`${BASE}/campaigns?${params.toString()}`);
  },

  // Les erreurs apparues pendant une campagne donnée.
  async getCampaignSignatures(id) {
    return getJson(`${BASE}/campaigns/${encodeURIComponent(id)}/signatures`);
  },

  async getTeams() {
    return getJson(`${BASE}/teams`);
  },

  async updateSignature(id, { ownerTeam, wiId, status, mutedUntil }) {
    return patchJson(`${BASE}/signatures/${encodeURIComponent(id)}`, { ownerTeam, wiId, status, mutedUntil });
  },

  // Le triage d'un probleme : le meme statut, la meme equipe et le meme ticket
  // sur toutes ses variantes, en une transaction cote serveur. Les filtres
  // sont obligatoires pour la meme raison que sur getProblemSignatures :
  // l'identifiant d'un probleme est calcule a la lecture et n'existe que dans
  // le perimetre qui l'a produit. Avec une autre fenetre c'est un 404, jamais
  // une ecriture sur un groupe qu'on n'avait pas sous les yeux.
  async updateProblem(id, { env, service, status: statusFilter, since, until } = {}, payload) {
    const params = new URLSearchParams();
    if (env) params.set('env', env);
    if (service) params.set('service', service);
    if (statusFilter) params.set('status', statusFilter);
    if (since) params.set('since', since);
    if (until) params.set('until', until);
    return patchJson(`${BASE}/problems/${encodeURIComponent(id)}?${params.toString()}`, payload);
  }
};

export default logIntelligenceService;
